import React, { useState } from 'react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import ClickableIcon from '~components/ClickableIcon';
import { faPlus, faUsers, faX } from '~components/icon';
import Icon from '~components/icon/Icon';
import Link from '~components/Link';
import Select from '~components/Select';

interface ComponentProps {
  id: string;
  type: 'users' | 'clients';
  value: any;
  icon: IconDefinition;
  setFieldValue: any;
  fullParticipantsList?: any[];
  selectOptions?: {
    label: string;
    value: number;
  }[];
}

const GroupList: React.FC<ComponentProps> = ({ value, id, type, icon, selectOptions, setFieldValue }) => {
  const [selectedValue, setSelectedValue] = useState<number | null>(null);

  const handleSelectChange = (_name: string, value: number, _label: string) => {
    setSelectedValue(value || null);
  };

  const onAdd = () => {
    if (selectedValue) {
      const selected = selectOptions?.find((opt) => opt.value === selectedValue);
      if (!selected) return;

      const newItem = {
        full_name: selected.label,
        [type === 'users' ? 'user_id' : 'client_id']: selected.value,
        isNew: true,
      };
      setFieldValue?.(type, [...(value || []), newItem]);
      setSelectedValue(null);
    }
  };

  const buildListItem = (item: any) => {
    const { id, full_name, service_type, start_time, end_time, isNew } = item;

    const participantId = item[type === 'users' ? 'user_id' : 'client_id'];

    return (
      <div className="list-row" key={id}>
        <Icon className="participant-icon" icon={icon} />

        {isNew ? (
          <>
            <span className="name">{full_name}</span>
            <span>-</span>
            <span className="details new">
              New Appointment
              <ClickableIcon
                className="remove-icon"
                id={`${id}-remove-icon`}
                icon={faX}
                onClick={() => onRemove(participantId)}
                color={null}
                toolTip="Remove Participant"
                dataCy={`${id}-remove-icon`}
              />
            </span>
          </>
        ) : (
          <>
            <Link className="name linked" to={`/appointments/${id}`}>
              {full_name}
            </Link>
            <span>-</span>
            <span className="details">
              {service_type?.name}, {start_time} - {end_time}
            </span>
          </>
        )}
      </div>
    );
  };

  const onRemove = (id: string) => {
    setFieldValue?.(
      type,
      value.filter((item: any) => {
        const itemId = type === 'users' ? item.user_id : item.client_id;
        return itemId !== id;
      }),
    );
  };

  return (
    <div className="group-list">
      <div className="top-row">
        <div className="title">Group {type === 'users' ? 'Care Workers' : 'Clients'}</div>
        <div className="end-section">
          <Select
            className="select"
            id={`${id}-select`}
            name={`${id}-select`}
            options={selectOptions}
            value={selectedValue}
            onChange={handleSelectChange}
            isClearable
          />
          <ClickableIcon
            className="confirm-add-icon"
            color={null}
            id={`${id}-confirm-add-icon`}
            icon={faPlus}
            onClick={onAdd}
            disabled={!selectedValue}
            toolTip="Add Participant"
            dataCy={`${id}-add-icon`}
          />
        </div>
      </div>
      <hr />
      <div className="list-items" data-cy={`${id}-list-items`}>
        {value && value.length > 0 ? (
          value.map((item: any) => buildListItem(item))
        ) : (
          <div className="missing-participants">
            <Icon icon={faUsers} />
            <span>No Participants Found</span>
            <span className="subtext">Please use Quick Selector to add participants on save</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupList;
